import { MouseEvent, PropsWithChildren } from 'react';
import styled from 'styled-components';

import { ButtonVariant, FormInputVariants } from '#/service/constants';
import { remify } from '#/service/utils';

import { FormButton, FormMarkdown } from './Form.styled';
import { FormGroupButtonProps } from './Form.types';

/** Disclaimer box for form buttons */
const Disclaimer = styled.div`
  background: var(--pageSecondaryBackground);
  padding: ${remify(12)};
  border-radius: ${remify(8)};
  display: flex;
  flex-direction: column;
  gap: ${remify(12)};
  text-align: center;

  /* trim outside margins from markdown content */
  & ${FormMarkdown} {
    margin: ${remify(-3)} 0;
  }
`;

/** Conditionally wraps a form button in a disclaimer box */
export const FormButtonDisclaimer = ({
  disclaimer,
  children,
}: PropsWithChildren<{ disclaimer?: string }>) => {
  if (!disclaimer) {
    return children;
  }

  return (
    <Disclaimer data-testid="formButtonDisclaimer" id="formButtonDisclaimer">
      <FormMarkdown>{disclaimer}</FormMarkdown>
      {children}
    </Disclaimer>
  );
};

export const FormGroupButton = ({
  activeButtonKey,
  button,
  handleSubmit,
  getFormData,
  isButtonDisabled,
}: FormGroupButtonProps) => {
  const { key, disclaimer, text, isMainButton } = button;
  const isActive = key === activeButtonKey;
  const isSubmit = button.variant === FormInputVariants.SUBMIT;
  const isPrimary = isSubmit || isMainButton;
  const isDisabled = isButtonDisabled(button);

  const getClickHandler =
    button.variant === FormInputVariants.SUBMIT
      ? (event: MouseEvent) => {
          handleSubmit(button.key, button.onClick, text);
          // submit buttons are rendered with `type="submit"`, but we handle the submission ourselves,
          // via `button.onClick`, so we need preventDefault to avoid double submission.
          event?.preventDefault();
        }
      : () => button.onClick(getFormData(), text);

  return (
    <FormButtonDisclaimer disclaimer={disclaimer} key={key}>
      <FormButton
        aria-describedby={disclaimer ? 'formButtonDisclaimer' : undefined}
        data-testid="form-button"
        disabled={(isActive || isDisabled) && isPrimary}
        isLoading={isActive}
        onClick={getClickHandler}
        type={isSubmit ? 'submit' : 'button'}
        variant={isPrimary ? ButtonVariant.Primary : ButtonVariant.Secondary}
      >
        {text}
      </FormButton>
    </FormButtonDisclaimer>
  );
};
